import { useStore } from '../../state/store';
import { describeRule, severityPill } from '../../lib/derive';
import { formatAgo } from '../../lib/format';
import {
  Heading,
  Panel,
  Pill,
  Placeholder,
  QuietButton,
  SolidButton,
  Sub,
  tabularNums,
} from '../ui';

export function Alerts() {
  const alerts = useStore((s) => s.alerts);
  const rules = useStore((s) => s.rules);
  const now = useStore((s) => s.now);
  const acknowledge = useStore((s) => s.acknowledgeAlert);
  const toggleRule = useStore((s) => s.toggleRule);
  const select = useStore((s) => s.select);
  const setView = useStore((s) => s.setView);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))',
        gap: 16,
        alignItems: 'start',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
        {alerts.length > 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 6,
              flexWrap: 'wrap',
              padding: '0 4px',
              fontSize: 12.5,
              color: 'var(--faint)',
            }}
          >
            <span>{alerts.length} active · newest first</span>
            {alerts.length >= 100 ? (
              <span>
                — the bridge keeps the most recent 100, older ones have been dropped
              </span>
            ) : null}
          </div>
        ) : null}

        <div
          className="scroll-y"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            // A quiet host shows two cards; a noisy one shows a hundred. Bound
            // the column so the rules panel beside it stays reachable.
            maxHeight: 'calc(100vh - 140px)',
          }}
        >
          {alerts.length === 0 ? (
            <Panel style={{ padding: 0 }}>
              <Placeholder
                title="Nothing firing"
                detail="Rules are evaluated on the bridge once per status frame. An alert raised while this tab is closed will still be waiting here."
              />
            </Panel>
          ) : (
            alerts.map((alert) => {
              const tone = severityPill(alert.severity);
              return (
                <Panel key={alert.id} style={{ padding: '22px 24px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Pill bg={tone.bg} fg={tone.fg} strong>
                      {tone.label}
                    </Pill>
                    <Heading size={16} style={{ letterSpacing: 'normal' }}>
                      {alert.title}
                    </Heading>
                    <div
                      style={{
                        marginLeft: 'auto',
                        fontSize: 12.5,
                        color: 'var(--faint)',
                        ...tabularNums,
                      }}
                    >
                      {formatAgo(alert.triggeredAt, now)}
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      color: 'var(--muted)',
                      marginTop: 12,
                      maxWidth: '62ch',
                      lineHeight: 1.55,
                      textWrap: 'pretty',
                    }}
                  >
                    {alert.detail}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      marginTop: 16,
                      flexWrap: 'wrap',
                    }}
                  >
                    {alert.processId !== null ? (
                      <SolidButton
                        onClick={() => {
                          setView('overview');
                          select(alert.processId!);
                        }}
                      >
                        Inspect {alert.processName ?? `#${alert.processId}`}
                      </SolidButton>
                    ) : null}
                    <QuietButton onClick={() => void acknowledge(alert.id)}>
                      Dismiss
                    </QuietButton>
                  </div>
                </Panel>
              );
            })
          )}
        </div>
      </div>

      <Panel style={{ padding: '22px 24px' }}>
        <Heading>Notify me when</Heading>
        <Sub>Rules live on the bridge — the phone and this console share them</Sub>

        <div style={{ marginTop: 16 }}>
          {rules.map((rule) => {
            const copy = describeRule(rule);
            return (
              <button
                key={rule.id}
                type="button"
                role="switch"
                aria-checked={rule.enabled}
                onClick={() => void toggleRule(rule.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '14px 0',
                  borderTop: '1px solid var(--line-soft)',
                  width: '100%',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{copy.label}</div>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: 'var(--faint)',
                      marginTop: 2,
                    }}
                  >
                    {copy.detail}
                  </div>
                </div>
                <div
                  style={{
                    width: 40,
                    height: 23,
                    flex: 'none',
                    borderRadius: 999,
                    position: 'relative',
                    background: rule.enabled ? 'var(--pos)' : 'var(--line)',
                    transition: 'background 140ms',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 2.5,
                      left: rule.enabled ? 19.5 : 2.5,
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      background: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                      transition: 'left 140ms',
                    }}
                  />
                </div>
              </button>
            );
          })}
        </div>

        <div
          style={{
            marginTop: 16,
            padding: '14px 16px',
            borderRadius: 13,
            background: 'var(--page)',
            fontSize: 12.5,
            color: 'var(--muted)',
            lineHeight: 1.55,
            textWrap: 'pretty',
          }}
        >
          Toggling a rule here changes it for every client, including the phone's push
          notifications. Thresholds and per-process scoping are edited in the app.
        </div>
      </Panel>
    </div>
  );
}
